import { useUser } from "@app/store/userStore";
import { Avatar } from "@design-system/DataDisplay/Avatar";
import { ProgressBar } from "@design-system/DataDisplay/ProgressBar/ProgressBar";
import { Skeleton } from "@design-system/DataDisplay/Skeleton";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import { useMemo, type FC } from "react";
import { makeCompletionSegments, type TChartData } from "./CompletionChart";
import { useQuestionnaireStatsTranslations } from "./i18n";

export const UsersActivityChart: FC<{
    chartData: TChartData;
    truncateAfter?: number;
}> = ({ chartData, truncateAfter }) => {
    const { t, keys } = useQuestionnaireStatsTranslations();
    const dataRows = useMemo(() => {
        if (!truncateAfter) return chartData.dataRows;
        return chartData.dataRows.slice(0, truncateAfter);
    }, [chartData.dataRows, truncateAfter]);
    return (
        <Box className="grid grid-cols-[9fr_16fr] gap-x-4 gap-y-2">
            <Text variant="caption-bold">
                {t(keys.users_activity_chart_assignee_header)}
            </Text>
            <Text variant="caption-bold">
                {t(
                    keys.users_activity_chart_completed_assigned_questions_header,
                )}
            </Text>
            {dataRows.map((row) => (
                <UserActivityRow
                    key={row.userId}
                    row={row}
                    seriesConfig={chartData.seriesConfig}
                    chartMetadata={chartData.chartMetadata}
                    seriesKeys={chartData.seriesKeys}
                />
            ))}
        </Box>
    );
};

const UserActivityRow: FC<{
    row: TChartData["dataRows"][number];
    seriesConfig: TChartData["seriesConfig"];
    chartMetadata: TChartData["chartMetadata"];
    seriesKeys: string[];
}> = ({ row, seriesConfig, chartMetadata, seriesKeys }) => {
    const { t, keys } = useQuestionnaireStatsTranslations();
    const userId = row.userId.toString();

    const user = useUser(userId);

    const completed = row.validated ?? 0;
    const total = useMemo(() => {
        const userIdx = chartMetadata?.userIds?.findIndex(
            (id) => id === userId,
        );
        if (userIdx === -1 || userIdx === undefined) return 0;
        return chartMetadata?.totalQuestionsByIdx?.[userIdx] ?? 0;
    }, [userId, chartMetadata]);

    const series = useMemo(() => {
        return makeCompletionSegments({
            dataRows: [row],
            seriesKeys,
            seriesConfig,
            chartMetadata: { totalQuestions: total },
            t,
            keys,
        });
    }, [row, seriesConfig, chartMetadata, seriesKeys, t, keys, total]);

    return (
        <>
            <FlexRow gap="2" alignItems="center" w="full">
                <Avatar
                    className="border border-white"
                    imageUrl={user?.avatar?.signedUrl}
                    variant="circle"
                    size="sm"
                    letter={user?.firstName.charAt(0)}
                />
                {user?.email ? (
                    <Ellipsis asChild hasTooltip>
                        <Text variant="body-sm-bold">{user?.email}</Text>
                    </Ellipsis>
                ) : (
                    <Skeleton className="w-20 h-4" />
                )}
            </FlexRow>
            <Box
                className="grid grid-cols-[1fr_auto]"
                gap="4"
                w="full"
                alignItems="center"
                justifyContent="stretch"
            >
                <FlexCol className="w-full">
                    <ProgressBar className="flex-1" segments={series} />
                </FlexCol>
                <Text variant="body-sm">
                    {t(
                        keys.users_activity_chart_completed_assigned_questions_total_label,
                        {
                            completed,
                            total,
                        },
                    )}
                </Text>
            </Box>
        </>
    );
};
